import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAppText } from 'src/i18n';
import { showMessage } from '../../components/components/SnackbarNotification/snackbarNotificationSlice';

export interface ReferenceYearDto {
    [key: string]: any;
    id: number;
    calendarYear: number;
    nameFr: string;
    nameEn: string;
    startDate: string;
    endDate: string;
    cutoffDate: string;
    openingOfOverall: string;
    closingOfOverall: string;
    isDeleted: boolean;
    isDuplicated: boolean;
}

export interface ReferenceYearState {
    referenceYears: ReferenceYearDto[];
    referenceYear: ReferenceYearDto;
    gettingReferenceYears: boolean;
    savingReferenceYear: boolean;
    gettingReferenceYear: boolean;
    archivingReferenceYear: boolean;
    selectedReferenceYearId: number;
    selectedReferenceYear: ReferenceYearDto;
    gettingCurrentReferenceYear: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    savingStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
    error?: string;
}

export const blankReferenceYearDto: ReferenceYearDto = {
    id: 0,
    calendarYear: 0,
    nameFr: '',
    nameEn: '',
    region_Id: 0,
    startDate: new Date().toJSON(),
    endDate: new Date().toJSON(),
    cutoffDate: new Date().toJSON(),
    openingOfOverall: new Date().toJSON(),
    closingOfOverall: new Date().toJSON(),
    isDeleted: false,
    isDuplicated: false,
};

const initialState: ReferenceYearState = {
    referenceYears: [],
    referenceYear: blankReferenceYearDto,
    savingReferenceYear: false,
    gettingReferenceYears: false,
    gettingReferenceYear: false,
    archivingReferenceYear: false,
    selectedReferenceYearId: 0,
    selectedReferenceYear: blankReferenceYearDto,
    status: 'idle',
    savingStatus: 'idle',
    error: undefined,
    gettingCurrentReferenceYear: false,
};

export const referenceYearSlice = createSlice({
    name: 'referenceYears',
    initialState,
    reducers: {
        clearReferenceYears: (state) => {
            state.referenceYears = [];
            state.referenceYear = blankReferenceYearDto;
        },
        updateReferenceYear: (state, param) => {
            const { payload } = param;
            state.referenceYear = payload;
        },
        setSelectedReferenceYear: (state, param) => {
            const { payload } = param;
            state.selectedReferenceYearId = payload;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(getReferenceYears.pending, (state, action) => {
                state.gettingReferenceYears = true;
                state.status = 'loading';
            })
            .addCase(getReferenceYears.fulfilled, (state, action) => {
                state.gettingReferenceYears = false;
                state.referenceYears = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getReferenceYears.rejected, (state, action) => {
                state.gettingReferenceYears = false;
                state.referenceYears = [];
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(saveReferenceYear.pending, (state, action) => {
                state.savingReferenceYear = true;
                state.savingStatus = 'loading';
            })
            .addCase(saveReferenceYear.fulfilled, (state, action) => {
                state.savingReferenceYear = false;
                state.savingStatus = 'succeeded';
            })
            .addCase(saveReferenceYear.rejected, (state, action) => {
                state.savingReferenceYear = false;
                state.savingStatus = 'failed';
                state.error = action.error.message;
            })
            .addCase(getReferenceYear.pending, (state, action) => {
                state.gettingReferenceYear = true;
                state.status = 'loading';
            })
            .addCase(getReferenceYear.fulfilled, (state, action) => {
                state.gettingReferenceYear = false;
                state.referenceYear = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getReferenceYear.rejected, (state, action) => {
                state.gettingReferenceYear = false;
                state.referenceYear = blankReferenceYearDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getReferenceYearInfo.pending, (state, action) => {
                state.gettingReferenceYear = true;
                state.status = 'loading';
            })
            .addCase(getReferenceYearInfo.fulfilled, (state, action) => {
                state.gettingReferenceYear = false;
                state.selectedReferenceYear = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getReferenceYearInfo.rejected, (state, action) => {
                state.gettingReferenceYear = false;
                state.referenceYear = blankReferenceYearDto;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(archiveReferenceYear.pending, (state, action) => {
                state.archivingReferenceYear = true;
                state.status = 'loading';
            })
            .addCase(archiveReferenceYear.fulfilled, (state, action) => {
                state.archivingReferenceYear = false;
                state.referenceYears = state.referenceYears.filter((x) => x.id !== action.payload.id);
                state.status = 'succeeded';
            })
            .addCase(archiveReferenceYear.rejected, (state, action) => {
                state.archivingReferenceYear = false;
                state.status = 'failed';
                state.error = action.error.message;
            })
            .addCase(getCurrentReferenceYear.pending, (state, action) => {
                state.gettingCurrentReferenceYear = true;
                state.status = 'loading';
            })
            .addCase(getCurrentReferenceYear.fulfilled, (state, action) => {
                state.gettingCurrentReferenceYear = false;

                if (state.selectedReferenceYearId === 0) {
                    state.selectedReferenceYearId = action.payload.id;
                }

                state.status = 'succeeded';
            })
            .addCase(getCurrentReferenceYear.rejected, (state, action) => {
                state.gettingCurrentReferenceYear = false;
                state.status = 'failed';
                state.error = action.error.message;
            });
    },
});

export const { clearReferenceYears } = referenceYearSlice.actions;
export const { updateReferenceYear } = referenceYearSlice.actions;
export const { setSelectedReferenceYear } = referenceYearSlice.actions;

export const getReferenceYears = createAsyncThunk('referenceYears/getReferenceYears', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/reference-years`);

    return response.data;
});

export const getReferenceYearInfo = createAsyncThunk('referenceYears/getReferenceYearInfo', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/reference-years/${id}`);

    return response.data;
});

export const getReferenceYear = createAsyncThunk('referenceYears/getReferenceYear', async (id: number, thunkAPI) => {
    const response = await axios.get(`/api/synergy/reference-years/${id}`);

    return response.data;
});

export const archiveReferenceYear = createAsyncThunk('referenceYears/archiveReferenceYear', async (id: number, thunkAPI) => {
    const response = await axios.post(`/api/synergy/reference-years/archive/${id}`);
    thunkAPI.dispatch(
        showMessage({
            type: 'info',
            message: getAppText('archivedReferenceYear'),
        })
    );
    return response.data;
});

export const getCurrentReferenceYear = createAsyncThunk('referenceYears/getCurrentReferenceYear', async (_, thunkAPI) => {
    const response = await axios.get(`/api/synergy/reference-years/get-current`);
    return response.data;
});

export const saveReferenceYear = createAsyncThunk(
    'referenceYears/saveReferenceYear',
    async (referenceYearToSave: ReferenceYearDto, thunkAPI) => {
        if (referenceYearToSave.id === 0) {
            const response = await axios.post(`/api/synergy/reference-years/create`, referenceYearToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('createReferenceYear'),
                })
            );
            return response.data;
        } else {
            const response = await axios.put(`/api/synergy/reference-years/put`, referenceYearToSave);
            thunkAPI.dispatch(
                showMessage({
                    type: 'info',
                    message: getAppText('saveReferenceYear'),
                })
            );
            return response.data;
        }
    }
);
